import { IconStop } from "@/assets/icons/geist/IconStop";
import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/podkit/toasts/use-toast";
import {
    type PlainTask,
    type PlainTaskExecution,
    useStartTask,
    useStopTaskExecution,
} from "@/queries/automations-queries";
import { formatError } from "@/utils/errors";
import { type FC, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TaskExecutionPhase } from "gitpod-next-api/gitpod/v1/environment_automation_pb.ts";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh.tsx";
import { Tooltip } from "@/components/Tooltip";

export const EnvironmentTaskRunActions: FC<{
    taskExecution: PlainTaskExecution;
    task: PlainTask;
    environmentId: string;
    disabled: boolean;
}> = ({ taskExecution, task, environmentId, ...props }) => {
    const stopTaskExecution = useStopTaskExecution();
    const startTask = useStartTask();
    const { toast } = useToast();
    const navigate = useNavigate();

    const onStop = useCallback(async () => {
        await stopTaskExecution.mutateAsync(
            { taskExecutionId: taskExecution.id, environmentId },
            {
                onError: (error: Error) =>
                    toast({
                        title: `Failed to stop ${task.metadata?.name || "task run"}`,
                        description: formatError(error),
                    }),
            },
        );
    }, [toast, stopTaskExecution, taskExecution, task, environmentId]);

    const onRestart = useCallback(async () => {
        const started = await startTask.mutateAsync(
            task.id,
            {
                onError: (error: Error) =>
                    toast({
                        title: `Failed to restart ${task.metadata?.name || "task"}`,
                        description: formatError(error),
                    }),
            });
        navigate(`/details/${environmentId}/task/${task.id}/run/${started.id}`);
    }, [startTask, task.id, task.metadata?.name, navigate, environmentId, toast]);

    const finished =
        taskExecution.status?.phase === TaskExecutionPhase.SUCCEEDED ||
        taskExecution.status?.phase === TaskExecutionPhase.FAILED ||
        taskExecution.status?.phase === TaskExecutionPhase.STOPPED;
    const stopping = stopTaskExecution.isPending || (!finished && taskExecution.spec?.desiredPhase !== TaskExecutionPhase.RUNNING);

    return (
        <Tooltip content={props.disabled ? "Environment is not running" : ""}>
            <div className="flex gap-2 self-center">
                {!finished && (
                    <Button
                        size="sm"
                        variant="secondary"
                        className="min-h-7"
                        onClick={onStop}
                        disabled={props.disabled}
                        loading={stopping}
                        LeadingIcon={IconStop}
                    >
                        Stop
                    </Button>
                )}
                {finished && (
                    <Button
                        size="sm"
                        variant="secondary"
                        className="min-h-7"
                        onClick={onRestart}
                        disabled={props.disabled}
                        loading={startTask.isPending}
                        LeadingIcon={IconRefresh}
                    >
                        Re-run task
                    </Button>
                )}
            </div>
        </Tooltip>
    );
};
